import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../lib/api';

/**
 * Per-attempt partner delivery log. Sourced from partner_events via
 * /api/deliveries. Gives operators visibility into HTTP status, response body
 * and outcome of every forwarded message without needing pod-level log access.
 */

type AttemptOutcome =
  | 'delivered'
  | 'failed'
  | 'retried'
  | 'dlq'
  | 'filtered'
  | 'no_provider'
  | 'skipped_log_only';

interface DeliverySummary {
  id: number;
  timestamp: string;
  alertType: string;
  clientId: string;
  severity: string;
  routingKey: string;
  organizationId: string | null;
  providerId: string | null;
  forwardedToPartner: boolean;
  partnerResponseTimeMs: number | null;
  retryCount: number;
  httpStatusCode: number | null;
  backendUrl: string | null;
  attemptOutcome: AttemptOutcome | null;
  error: string | null;
  errorSummary: string | null;
}

interface DeliveryDetail extends DeliverySummary {
  responseBodyExcerpt: string | null;
  payload: any;
}

type StatusFilter = 'all' | 'success' | '4xx' | '5xx' | 'network' | 'skipped';

const STATUS_PILLS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'success', label: 'Success (2xx)' },
  { id: '4xx', label: '4xx' },
  { id: '5xx', label: '5xx' },
  { id: 'network', label: 'Network / No status' },
  { id: 'skipped', label: 'Skipped / Filtered' },
];

function statusBadge(d: DeliverySummary) {
  if (d.httpStatusCode) {
    const code = d.httpStatusCode;
    if (code >= 200 && code < 300) {
      return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-green-100 text-green-700">{code}</span>;
    }
    if (code >= 400 && code < 500) {
      return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-amber-100 text-amber-700">{code}</span>;
    }
    if (code >= 500) {
      return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-red-100 text-red-700">{code}</span>;
    }
    return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700">{code}</span>;
  }
  if (d.attemptOutcome === 'filtered' || d.attemptOutcome === 'no_provider' || d.attemptOutcome === 'skipped_log_only') {
    return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-500">skip</span>;
  }
  return <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-red-100 text-red-700">net</span>;
}

function outcomeBadge(outcome: AttemptOutcome | null) {
  if (!outcome) return <span className="text-gray-400">—</span>;
  const colors: Record<AttemptOutcome, string> = {
    delivered: 'bg-green-50 text-green-700 border-green-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    retried: 'bg-amber-50 text-amber-700 border-amber-200',
    dlq: 'bg-red-100 text-red-800 border-red-300',
    filtered: 'bg-gray-50 text-gray-600 border-gray-200',
    no_provider: 'bg-gray-50 text-gray-600 border-gray-200',
    skipped_log_only: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-xs ${colors[outcome]}`}>
      {outcome}
    </span>
  );
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export const DeliveryLogPage: React.FC = () => {
  const [deliveries, setDeliveries] = useState<DeliverySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [providerFilter, setProviderFilter] = useState('');
  const [alertTypeFilter, setAlertTypeFilter] = useState('');
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [limit] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshIntervalSec] = useState(5);

  const [selected, setSelected] = useState<DeliveryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (providerFilter.trim()) params.set('providerId', providerFilter.trim());
    if (alertTypeFilter.trim()) params.set('alertType', alertTypeFilter.trim());
    if (clientIdFilter.trim()) params.set('clientId', clientIdFilter.trim());
    return params.toString();
  }, [statusFilter, providerFilter, alertTypeFilter, clientIdFilter, limit]);

  const load = useCallback(async () => {
    try {
      const resp = await apiCall(`/api/deliveries?${queryString}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setDeliveries(data.deliveries || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, refreshIntervalSec * 1000);
    return () => clearInterval(t);
  }, [autoRefresh, refreshIntervalSec, load]);

  const openDetail = useCallback(async (row: DeliverySummary) => {
    setSelected({ ...row, responseBodyExcerpt: null, payload: null });
    setDetailLoading(true);
    try {
      const resp = await apiCall(`/api/deliveries/${row.id}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setSelected(data.delivery);
    } catch (err: any) {
      setSelected((prev) => (prev ? { ...prev, error: err.message } : prev));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = useCallback(() => setSelected(null), []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Delivery Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every partner delivery attempt, with HTTP status, response body and outcome.
          Sourced from <code className="font-mono text-xs">partner_events</code>.
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_PILLS.map((p) => (
            <button
              key={p.id}
              onClick={() => setStatusFilter(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                statusFilter === p.id
                  ? 'bg-[#1D407B] text-white border-[#1D407B]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Provider ID"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
          />
          <input
            type="text"
            placeholder="Alert type"
            value={alertTypeFilter}
            onChange={(e) => setAlertTypeFilter(e.target.value)}
            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
          />
          <input
            type="text"
            placeholder="Client ID"
            value={clientIdFilter}
            onChange={(e) => setClientIdFilter(e.target.value)}
            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh ({refreshIntervalSec}s)
          </label>
          <button
            onClick={load}
            className="ml-auto px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left">
              <th className="px-4 py-3 font-semibold text-gray-600">Time</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Alert</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Client</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Provider</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Backend URL</th>
              <th className="px-4 py-3 font-semibold text-gray-600">HTTP</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Dur</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Retry</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Outcome</th>
              <th className="px-4 py-3 font-semibold text-gray-600">Error</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : deliveries.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">
                  No deliveries match the current filters.
                </td>
              </tr>
            ) : (
              deliveries.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => openDetail(d)}
                  className="border-b border-gray-100 last:border-0 align-top cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">
                    {new Date(d.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{d.alertType}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 font-mono">{truncate(d.clientId, 16)}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">{d.providerId || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono" title={d.backendUrl || ''}>
                    {truncate(d.backendUrl, 40)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{statusBadge(d)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 font-mono whitespace-nowrap">
                    {d.partnerResponseTimeMs != null ? `${d.partnerResponseTimeMs}ms` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 font-mono">{d.retryCount}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{outcomeBadge(d.attemptOutcome)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 max-w-md">
                    {d.errorSummary || <span className="text-gray-400">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={closeDetail}
          />
          <div className="fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-gray-500 font-mono">Delivery #{selected.id}</div>
                <div className="text-lg font-semibold text-gray-900">{selected.alertType}</div>
              </div>
              <button
                onClick={closeDetail}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Timestamp" value={new Date(selected.timestamp).toLocaleString()} />
                <Field label="Outcome" node={outcomeBadge(selected.attemptOutcome)} />
                <Field label="HTTP Status" node={statusBadge(selected)} />
                <Field label="Duration" value={selected.partnerResponseTimeMs != null ? `${selected.partnerResponseTimeMs} ms` : '—'} />
                <Field label="Provider" value={selected.providerId || '—'} />
                <Field label="Retry Count" value={String(selected.retryCount)} />
                <Field label="Client ID" value={selected.clientId} />
                <Field label="Severity" value={selected.severity} />
                <Field label="Organization ID" value={selected.organizationId || '—'} />
                <Field label="Routing Key" value={selected.routingKey} />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Backend URL</div>
                <code className="block text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono break-all">
                  {selected.backendUrl || '—'}
                </code>
              </div>

              {selected.error && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Error</div>
                  <pre className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 whitespace-pre-wrap break-all">
                    {selected.error}
                  </pre>
                </div>
              )}

              {detailLoading ? (
                <div className="text-sm text-gray-400">Loading response body...</div>
              ) : (
                <>
                  {selected.responseBodyExcerpt && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                        Response Body (first 1000 chars)
                      </div>
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 whitespace-pre-wrap break-all max-h-64 overflow-auto">
                        {selected.responseBodyExcerpt}
                      </pre>
                    </div>
                  )}

                  {selected.payload && (
                    <details className="group">
                      <summary className="cursor-pointer text-xs uppercase tracking-wide text-gray-500 mb-1 hover:text-gray-700">
                        Notification Payload (click to expand)
                      </summary>
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 whitespace-pre-wrap break-all max-h-96 overflow-auto mt-2">
                        {typeof selected.payload === 'string'
                          ? selected.payload
                          : JSON.stringify(selected.payload, null, 2)}
                      </pre>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value?: string; node?: React.ReactNode }> = ({ label, value, node }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5">{node ?? value ?? '—'}</div>
  </div>
);
